<template>
  <NuxtLayout name="default">
    <BlocksBlockRichText
      v-if="errorRichTextModuleData"
      :data="errorRichTextModuleData as RichTextModuleData"
      class="error-view-rich-text"
    />

    <div v-else class="max-w-200 container mx-auto px-4 py-10">
      <div class="py-6 lg:py-10">
        <h1 class="ui-h3 mb-6">
          {{ errorData.title }}
        </h1>
        <ul class="list-disc space-y-3 pl-8">
          <li
            v-for="(step, idx) in errorDescriptionSteps"
            :key="idx"
            @click="contentLink"
            v-html="step"
          />
        </ul>
        <div class="hidden" data-error-text>
          {{ error?.message }}
        </div>
        <div class="hidden" data-error-code>
          {{ error?.statusCode }}
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { NuxtError } from "#app";
import { RichTextModuleData } from "ecom-types";

const props = defineProps<{
  error: NuxtError;
}>();

const { t } = useI18n();
const { $api } = useNuxtApp();
const generalStore = useGeneralStore();
const { filesListLinks, filesListMeta } = useMetaIcons();

const { data: apiData } = await useAsyncData(
  `global-error-${props.error?.statusCode}`,
  () => {
    return $api.blocks.getBlock(`global-error-${props.error?.statusCode}`);
  },
);

const errorRichTextModuleData = computed(() => {
  return apiData.value?.data?.currentTranslation?.content;
});

useHead({
  link: [...filesListLinks],
  meta: [...filesListMeta],
});

useSeoMeta({
  title: () => generalStore.settings?.company_name || "",
  ogTitle: () => generalStore.settings?.company_name || "",
  description: () => generalStore.settings?.company_name || "",
  ogDescription: () => generalStore.settings?.company_name || "",
});

// const handleError = () => clearError({ redirect: "/" });

const errorData = computed(() => {
  switch (props.error?.statusCode) {
    case 400:
      return {
        title: t("yourRequestCouldNotBeUnderstood"),
      };
    case 403:
      return {
        title: t("youDoNotHavePermission"),
      };
    case 404:
      return {
        title: t("notFoundTitle"),
      };
    case 500:
      return {
        title: t("internalServerError", { status: props.error.statusCode }),
      };
    case 502:
      return {
        title: t("badGateway", { status: props.error.statusCode }),
      };
    case 503:
      return {
        title: t("serviceUnavailable", { status: props.error.statusCode }),
      };
    case 504:
      return {
        title: t("gatewayTimeout", { status: props.error.statusCode }),
      };
    case 505:
      return {
        title: t("httpVersionNotSupported", { status: props.error.statusCode }),
      };
    case 522:
      return {
        title: t("connectionTimedOut", { status: props.error.statusCode }),
      };
    default:
      return {
        title: `${props.error?.statusCode} ${props.error?.message}`,
      };
  }
});

const contentLink = useContentLink;

const errorDescriptionSteps = [
  t("ifYouTypedTheUrlDirectlyPleaseMakeSureTheSpellingIsCorrect"),
  t("ifYouClickedOnALinkToGetHere,WeMustHaveMovedTheContent"),
  t("pleaseTryOurStoreSearchBoxAboveToSearchForAnItem"),
  t(
    "ifYouAreNotSureHowYouGotHereTryStartingAtOurHomepageToNavigateToWhatYouAreLookingFor",
  ),
];
</script>

<style>
.error-view-rich-text,
.error-view-rich-text .relative picture {
  @apply min-h-screen-without-header;
}

.error-view-rich-text .content {
  @apply h-full;
}
</style>
